import { initialValueEmptyChildren } from '@/sharedComponents/TextEditor/config';
import { Delete } from '@styled-icons/feather';
import { useDreamEditorRef } from '@typedream/dream-editor';
import { Button } from '@typedream/ui';
import { getNode, insertNodes, removeNodes, useEventPlateId } from '@udecode/plate-core';
import classNames from 'classnames';
import { cloneDeep } from 'lodash';
import { useState } from 'react';
import sharedStyles from '../../styles/Shared.module.css';

import { initialValueEmpty } from '../../sharedComponents/TextEditor';
import Popup from '../../sharedComponents/TextEditor/components/Popup';
import { DreamValue, useTPlateEditorRef } from '../../sharedComponents/TextEditor/plate';

interface EmptyPageButtonProps {
  onClick?: () => void;
}

function createEmptyContentWithNavbar(
  editor: ReturnType<typeof useDreamEditorRef> | ReturnType<typeof useTPlateEditorRef>
): DreamValue {
  if (!editor) return initialValueEmpty;
  const rootNode = getNode(editor, [0]);

  // if navbarNode exist, it will always be on [0,0] as of now
  const navbarNode = getNode(editor, [0, 0]);
  const isNavbarExist = navbarNode && navbarNode.type === 'navbar';
  if (!rootNode || !isNavbarExist) return initialValueEmpty;

  const clonedRoot = cloneDeep(rootNode);
  const clonedNavbar = cloneDeep(navbarNode);

  // remove all children except navbar
  const newEmptyPageWithNavbar = [
    {
      ...clonedRoot,
      background: undefined,
      backgroundColor: undefined,
      backgroundImage: undefined,
      backgroundDarken: undefined,
      backgroundVideo: undefined,
      children: [clonedNavbar, ...initialValueEmptyChildren],
    },
  ] as unknown as DreamValue; // TODO: remove typecasting

  return newEmptyPageWithNavbar;
}

function EmptyPageButton({ onClick }: EmptyPageButtonProps) {
  const [openEmptyPagePopup, setopenEmptyPagePopup] = useState(false);
  const lastFocusedEditorID = useEventPlateId();
  // if there's no editor focused/selected before, lastFocusedEditorID becomes
  // Symbol(plate). we need to somehow pass in editorID into this component
  // without relying on the last focused editor. or maybe we can focus on
  // the specific editor on load?
  const legacyEditor = useTPlateEditorRef(
    typeof lastFocusedEditorID === 'string' ? lastFocusedEditorID : 'main'
  );
  const dreamEditor = useDreamEditorRef();

  // const editor = ENABLE_DREAM_EDITOR ? dreamEditor : legacyEditor;
  const editor = dreamEditor || legacyEditor;

  return (
    <>
      <Button
        variant="ghost"
        onClick={() => setopenEmptyPagePopup(true)}
        data-testid="EmptyPageButton"
        leftIcon={<Delete size={18} className="mr-2 is-subtitle-size" />}
      >
        Empty Page
      </Button>
      <Popup
        width={400}
        height={260}
        open={openEmptyPagePopup}
        setopen={setopenEmptyPagePopup}
        className="p-6"
      >
        <div className="is-size-6 with-text-color mb-3">Delete the contents of this page?</div>
        <hr className={sharedStyles.hr} />
        <div className="is-subtitle with-light-text-color mb-3">This action cannot be undone.</div>
        <button
          type="button"
          className={classNames(
            'button',
            'is-danger',
            'with-danger-transparent-background-color',
            'with-danger-text-color',
            'has-font-weight-normal',
            'mt-2'
          )}
          onClick={() => {
            if (onClick) {
              onClick();
            } else {
              // Ideally these are handled by onClick.
              // Keeping it here for backward compatibility
              // (before the component accept onClick prop),
              // without modifying components that use this EmptyPageButton.
              // Maybe we want to change this in the future.
              try {
                const emptyContent = createEmptyContentWithNavbar(editor);
                removeNodes(editor, { at: [0] });
                insertNodes(editor, emptyContent, { at: [0] });
              } catch (error) {
                /*  */
              }
            }

            setopenEmptyPagePopup(false);
          }}
          data-testid="EmptyPagePopup_confirm_empty_button"
        >
          Empty page
        </button>
      </Popup>
    </>
  );
}

EmptyPageButton.defaultProps = {
  onClick: undefined,
};

export default EmptyPageButton;
